import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { addMonths } from 'date-fns';
import { object, string } from 'yup';

import { ActionButtons } from '@hcs/design-system';
import {
  CreateUsageQueryForm,
  OrganizationapiSubmitUsageQueryRequest,
} from '@hcs/types';
import { isDateStr, parseDateString } from '@hcs/utils';

import { useOrgUsageQueryCreate } from '../../hooks/useOrgUsageQueryCreate';
import { OrgUsageQueryDetailsCreate } from '../OrgUsageQueryDetails';

interface OrgUsageQueryCreateProps {
  actionButtonsPortalId?: string;
  onClose: () => void;
  orgId: number;
  mode: 'internal' | 'external';
}

const YUP_SHEMA = object({
  application: string().nullable(),
  userEmail: string(),
  subjectType: string(),
  subjectId: string(),
  startReqTime: string()
    .required('From date is required')
    .test(
      'is-valid-date',
      'From date must be a valid date',
      (value: string | undefined) => {
        if (value == null) {
          return true;
        }
        return isDateStr(value);
      }
    ),
  endReqTime: string()
    .required('To date is required')
    .test(
      'is-valid-date',
      'To date must be a valid date',
      (value: string | undefined) => {
        if (value == null) {
          return true;
        }
        return isDateStr(value);
      }
    )
    .when('startReqTime', ([startReqTime]: (string | undefined)[], schema) => {
      if (startReqTime == null) {
        return schema;
      }
      // if returns true, no error is shown to user:
      return schema.test(
        'from-to-dates-compatible',
        'To date must be on or after from date',
        (endReqTime: string | undefined) => {
          if (endReqTime == null) {
            return true;
          }
          if (isDateStr(startReqTime) && isDateStr(endReqTime)) {
            return parseDateString(startReqTime) <= parseDateString(endReqTime);
          }
          return true;
        }
      );
    })
    .when('startReqTime', ([startReqTime]: (string | undefined)[], schema) => {
      if (startReqTime == null) {
        return schema;
      }
      // if returns true, no error is shown to user:
      return schema.test(
        'date-range-3-monnths-max',
        'Maximum supported date range is 3 months',
        (endReqTime: string | undefined) => {
          if (endReqTime == null) {
            return true;
          }
          if (isDateStr(startReqTime) && isDateStr(endReqTime)) {
            const start = parseDateString(startReqTime);
            const end = parseDateString(endReqTime);
            return addMonths(start, 3) >= end;
          }
          return true;
        }
      );
    }),
});

export const OrgUsageQueryCreate = ({
  actionButtonsPortalId,
  onClose,
  orgId,
  mode,
}: OrgUsageQueryCreateProps) => {
  const form = useForm<CreateUsageQueryForm>({
    resolver: yupResolver(YUP_SHEMA),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      application: null,
    },
  });

  const createUsageQuery = useOrgUsageQueryCreate(orgId);

  const handleSubmit = (data: CreateUsageQueryForm) => {
    const submitData = Object.keys(
      data
    ).reduce<OrganizationapiSubmitUsageQueryRequest>((accum, field) => {
      const typedFieldName = field as keyof CreateUsageQueryForm;
      const fieldValue = data[typedFieldName];
      // clear empty and null field values
      if (fieldValue !== '' && fieldValue !== null) {
        return {
          ...accum,
          [typedFieldName]: data[typedFieldName],
        };
      }
      return accum;
    }, {});
    createUsageQuery.mutate(submitData);
    onClose();
  };

  const dataHcName = 'org-query-create-dialog';
  return (
    <FormProvider {...form}>
      <form data-hc-name={`${dataHcName}-form`}>
        <OrgUsageQueryDetailsCreate mode={mode} orgId={orgId} />
        <ActionButtons
          portalIdRender={actionButtonsPortalId}
          dataHcName={`${dataHcName}-actions`}
          actions={[
            {
              dataHcName: `${dataHcName}-save`,
              label: 'Generate Report',
              type: 'submit',
              onClick: form.handleSubmit(handleSubmit),
              primary: true,
            },
          ]}
        />
      </form>
    </FormProvider>
  );
};
